/**
 * Demo data adapter (SKE-4166) — the seam between the public approvals demo
 * and the real External Review API.
 *
 * ============================ HARD SAFETY INVARIANT ==========================
 * The demo is served to anonymous, concurrent visitors over ONE shared,
 * read-only ExternalApproval link. Every mutation in demo mode MUST stay inside
 * that visitor's own localStorage. `demoPostAction` and `demoPostComment` issue
 * no network request at all and in particular never touch:
 *
 *   PUT   /v2/collaboration/external-review/{reviewKey}/{postId}/status/{statusKey}
 *   PATCH /v2/collaboration/external-review/{reviewKey}/{postId}/comments/{commentId}
 *
 * If they ever did, one visitor's click would change what every other visitor
 * (and the seeded demo account itself) sees. `src/api/index.ts` carries a
 * matching runtime guard as a second line of defence, and
 * `demoApi.test.ts` asserts the invariant against a recording fetch stub.
 * =============================================================================
 *
 * Reads are the opposite: the initial load pulls REAL data from the demo link
 * so the demo inherits real product fidelity (real media, real captions, real
 * previews). That snapshot is curated (see curation.ts) and cached per visitor;
 * every later read is served from the store.
 */

import { fetchPosts, fetchSetting } from 'api'

import {
  buildDemoStatus,
  buildInitialDemoComments,
  curateDemoPosts,
  isDemoTypingComment,
  makeStatusComment,
} from './curation'
import { trackDemo } from './demoAnalytics'
import type { DemoBrand } from './demoBrand'
import { getDemoUpstreamReviewKey } from './demoMode'
import type { DemoStore } from './demoStore'
import {
  clearDemoStore,
  createDemoStore,
  getDemoStoreSnapshot,
  readDemoStore,
  updateDemoStore,
  writeDemoStore,
} from './demoStore'
import type {
  DemoRawComment,
  DemoRawPost,
  DemoRawSettings,
  DemoStatusType,
} from './demoTypes'

/* -------------------------------------------------------------------------- */
/* Snapshot                                                                    */
/* -------------------------------------------------------------------------- */

let snapshotPromise: Promise<DemoStore> | null = null

/**
 * Load the visitor's snapshot, creating it from the live demo link on first
 * visit.
 *
 * Once a snapshot exists in the store we always return the LATEST persisted
 * copy (readDemoStore), so reads issued after a mutation observe that mutation.
 * The `snapshotPromise` memo exists ONLY to collapse the two calls Homepage
 * makes back-to-back on first load (`fetchSetting` then `fetchPosts`) into a
 * single upstream round-trip, before anything has been written to the store —
 * it must NOT be used as a durable read cache, because mutations write through
 * `writeDemoStore` (localStorage + memoryFallback) and never touch this memo.
 */
const ensureSnapshot = (): Promise<DemoStore> => {
  const current = readDemoStore(getDemoUpstreamReviewKey())
  if (current) {
    return Promise.resolve(current)
  }

  snapshotPromise ??= loadOrCreateSnapshot().catch((error: unknown) => {
    // Don't cache a failed load — a transient network error should not brick
    // the demo for the rest of the session.
    snapshotPromise = null
    throw error
  })

  return snapshotPromise
}

const loadOrCreateSnapshot = async (): Promise<DemoStore> => {
  // Only reached via `ensureSnapshot`'s memo, which has already confirmed no
  // stored snapshot exists — go straight to the upstream fetch (a second
  // readDemoStore here would be a guaranteed miss).
  const source = getDemoUpstreamReviewKey()

  const rawSettings = (await fetchSetting(source)) as unknown as DemoRawSettings
  const rawPosts = (await fetchPosts(source)) as unknown as DemoRawPost[]

  const settings = applyDemoSettingsOverrides(rawSettings)
  const posts = curateDemoPosts(rawPosts, settings.statuses)

  return writeDemoStore(
    createDemoStore({
      source,
      settings,
      posts,
      comments: buildInitialDemoComments(posts, settings.statuses),
    })
  )
}

/**
 * The upstream link is read-only in the database — deliberately, so nothing can
 * be mutated server-side even by accident. The demo still has to *look*
 * interactive, so the client-side snapshot flips `isReadOnly` off and turns the
 * conversation panel on. Safe precisely because demo mutations never leave the
 * browser (see the invariant at the top of this file).
 */
const applyDemoSettingsOverrides = (
  settings: DemoRawSettings
): DemoRawSettings => ({
  ...settings,
  // Any future setting that gates interactivity on the link being writable
  // (e.g. a `canReply`/`isExternalNotesEditable` flag) must be forced on here
  // too, or it will silently default off in the demo.
  isReadOnly: false,
  isConversationsEnabled: true,
  // The marketer's reply (phase 5) is an EXTERNAL note, and `Conversation.tsx`
  // filters those out when this is off. The seeded link's own value therefore
  // decides whether the demo's headline interaction is visible at all — so the
  // demo does not leave that to a setting nobody would think to check.
  isExternalNotesVisible: true,
})

/* -------------------------------------------------------------------------- */
/* Reads                                                                       */
/* -------------------------------------------------------------------------- */

export const demoFetchSetting = async (): Promise<unknown> =>
  (await ensureSnapshot()).settings

export const demoFetchPosts = async (): Promise<DemoRawPost[]> =>
  (await ensureSnapshot()).posts

/**
 * The calendar view reads the same curated set, so post statuses stay
 * consistent between the list and the calendar.
 */
export const demoFetchCalendar = async (): Promise<DemoRawPost[]> =>
  (await ensureSnapshot()).posts

export const demoFetchComment = async (
  postId: string
): Promise<DemoRawComment[]> => (await ensureSnapshot()).comments[postId] ?? []

/* -------------------------------------------------------------------------- */
/* Client-only mutations                                                       */
/* -------------------------------------------------------------------------- */

const statusTypeForKey = (
  settings: DemoRawSettings,
  statusKey: string
): DemoStatusType | undefined => {
  const statuses = settings.statuses ?? {}

  return (Object.keys(statuses) as DemoStatusType[]).find(
    (type) => statuses[type]?.key === statusKey
  )
}

/**
 * Approve / request-changes / undo — **localStorage only**. Returns the same
 * `{ post: { status } }` envelope the real endpoint returns so Homepage's
 * `result?.post?.status?.label` read is unchanged.
 */
export const demoPostAction = async (postId: string, statusKey: string) => {
  const store = await ensureSnapshot()
  const type = statusTypeForKey(store.settings, statusKey)

  if (!type) {
    throw new Error(
      `[external-review] demo: unknown status key "${statusKey}" — cannot apply action`
    )
  }

  const status = buildDemoStatus(store.settings.statuses, type)
  const previous = store.posts.find((post) => post.id === postId)?.status
  const now = new Date().toISOString()

  updateDemoStore(store, (current) => ({
    ...current,
    posts: current.posts.map((post) =>
      post.id === postId ? { ...post, status } : post
    ),
    comments: {
      ...current.comments,
      [postId]: [
        ...(current.comments[postId] ?? []),
        makeStatusComment({
          createdAt: now,
          oldStatus: previous ?? null,
          newStatus: status,
        }),
      ],
    },
    actions: [
      ...current.actions,
      { postId, status: type, at: now, kind: 'status' as const },
    ],
  }))

  // Counted HERE, at the one choke point every demo status change goes through,
  // rather than in the approver UI — so desktop and mobile are covered by one
  // call, the reviewer path (which never reaches this file) is not instrumented
  // by accident, and the event cannot be fired for an action that did not
  // actually land in the store.
  //
  // `pending` is the undo, and the taxonomy has no event for it: an undo is a
  // correction, not a decision, and counting it would double-count the visitor
  // who approves, undoes, then approves again.
  if (type !== 'pending') {
    trackDemo(
      type === 'approved' ? 'demo-post-approved' : 'demo-post-rejected',
      { post_id: postId, action_count: store.actions.length + 1 }
    )
  }

  return { post: { status } }
}

/**
 * Attach the visitor's note to a status change — **localStorage only**.
 *
 * Mirrors the real endpoint's semantics: it PATCHes `text` onto an existing
 * comment (the status-change entry) and returns that comment, which Homepage
 * feeds to `normalizeRepliedMessage`.
 */
export const demoPostComment = async (
  postId: string,
  comment: string,
  commentId: string
): Promise<DemoRawComment> => {
  const store = await ensureSnapshot()
  const existing = store.comments[postId] ?? []
  const target = existing.find((item) => item.id === commentId)

  const updated: DemoRawComment = target
    ? { ...target, text: comment }
    : makeStatusComment({
        id: commentId || undefined,
        text: comment,
        createdAt: new Date().toISOString(),
        oldStatus: null,
        newStatus: store.posts.find((post) => post.id === postId)?.status,
      })

  const currentType = store.posts.find((post) => post.id === postId)?.status
    ?.type

  updateDemoStore(store, (current) => ({
    ...current,
    comments: {
      ...current.comments,
      [postId]: target
        ? existing.map((item) => (item.id === commentId ? updated : item))
        : [...existing, updated],
    },
    // The note is an action in its own right — the marketer mirror surfaces
    // "your client left a note" separately from a status flip.
    actions: [
      ...current.actions,
      {
        postId,
        status: currentType ?? 'requestChange',
        at: updated.createdAt,
        kind: 'comment' as const,
        note: comment,
      },
    ],
  }))

  // Same choke-point reasoning as `demoPostAction`. Deliberately NO note text:
  // the note is a stranger's free text about their own business, so only its
  // length travels — enough to tell a real sentence from an empty submit.
  trackDemo('demo-comment-added', {
    post_id: postId,
    note_length: comment.trim().length,
    action_count: store.actions.length + 1,
  })

  notifyAiReplyHandler({ postId, commentId: updated.id, text: comment })

  return updated
}

/* -------------------------------------------------------------------------- */
/* Marketer replies (phase 5)                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Upsert one comment into a post's timeline — **localStorage only**, like every
 * other mutation on this file. Used by the AI reply flow to put the "typing…"
 * placeholder in and then swap the real reply over the top of it, which is why
 * it is an upsert rather than an append: same id, one entry, no flicker.
 *
 * Deliberately NOT added to the `actions` trail. The trail is "things the
 * visitor did", and the marketer mirror renders it as such; a reply the visitor
 * received is not one of their actions.
 */
export const upsertDemoComment = async (
  postId: string,
  comment: DemoRawComment
): Promise<DemoStore> => {
  const store = await ensureSnapshot()
  const existing = store.comments[postId] ?? []
  const hasComment = existing.some((item) => item.id === comment.id)

  return updateDemoStore(store, (current) => {
    const currentList = current.comments[postId] ?? []

    return {
      ...current,
      comments: {
        ...current.comments,
        [postId]: hasComment
          ? currentList.map((item) => (item.id === comment.id ? comment : item))
          : [...currentList, comment],
      },
    }
  })
}

/**
 * Drop every transient "typing…" placeholder from the snapshot.
 *
 * The placeholder is written to the store so it can render through the same
 * path as a real comment with no changes to the shared conversation
 * components — which means it is also persisted, and a visitor who closes the
 * tab mid-beat would otherwise come back to a marketer who has been typing
 * since Tuesday. Called once when the demo mounts, and again after every reply
 * settles, so a ghost can survive at most one page load's worth of nothing.
 *
 * Returns `null` when there was nothing to clean, so callers can skip a
 * needless re-render.
 */
export const clearDemoTypingComments = async (): Promise<DemoStore | null> => {
  const store = await ensureSnapshot()

  const hasTyping = Object.values(store.comments).some((list) =>
    list.some((comment) => isDemoTypingComment(comment))
  )

  if (!hasTyping) {
    return null
  }

  return updateDemoStore(store, (current) => ({
    ...current,
    comments: Object.fromEntries(
      Object.entries(current.comments).map(([postId, list]) => [
        postId,
        list.filter((comment) => !isDemoTypingComment(comment)),
      ])
    ),
  }))
}

/** The post's caption, so a reply can be about the post rather than generic. */
export const getDemoPostCaption = (postId: string): string => {
  const post = getDemoStoreSnapshot()?.posts.find((item) => item.id === postId)

  return typeof post?.caption === 'string' ? post.caption : ''
}

/* -------------------------------------------------------------------------- */
/* Personalization (phase 4)                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Re-skin the board to the visitor's own client — **localStorage only**, like
 * every other demo mutation on this file.
 *
 * Writes into `settings` rather than into a parallel store so the branding
 * flows through the code that already exists: `normalizeSetting` maps the raw
 * `company` field onto `companyName`, which is what the approver header
 * renders. The full `DemoBrand` is kept alongside under `demoBrand` (settings
 * carries an index signature, so it survives the round-trip) as the record of
 * what was applied — the marketer mirror and the personalization panel read it
 * from there.
 *
 * `resetDemo` clears the store, so resetting the board also un-brands it. That
 * is deliberate and is the ONLY way to un-brand: the reset is the "start over"
 * affordance, and personalization is part of the state it starts over from.
 * (The email unlock is stored separately and deliberately survives a reset —
 * see demoUnlock.ts.)
 */
export const applyDemoBrand = async (brand: DemoBrand): Promise<DemoStore> => {
  const store = await ensureSnapshot()

  return updateDemoStore(store, (current) => ({
    ...current,
    settings: {
      ...current.settings,
      company: brand.name,
      // Always a usable value: `resolveDemoBrand` falls back to a locally
      // generated monogram, so this never blanks the header's `<img>`.
      logoUrl: brand.logoUrl,
      demoBrand: brand,
    },
  }))
}

/**
 * The applied brand, or null when the visitor hasn't personalized yet.
 *
 * Identity is stable between writes — it is read straight off the stored
 * settings object, which only changes when `applyDemoBrand` runs — so this is
 * safe as a `useSyncExternalStore` snapshot.
 */
export const getDemoBrandSnapshot = (): DemoBrand | null => {
  const brand = getDemoStoreSnapshot()?.settings.demoBrand

  return brand && typeof brand === 'object' ? (brand as DemoBrand) : null
}

/* -------------------------------------------------------------------------- */
/* The AI marketer reply seam (filled by phase 5)                              */
/* -------------------------------------------------------------------------- */

export interface DemoAiReplyContext {
  postId: string
  commentId: string
  text: string
}

export type DemoAiReplyHandler = (context: DemoAiReplyContext) => void

let aiReplyHandler: DemoAiReplyHandler | null = null

/**
 * Register the "AI marketer replies to the client's comment" behaviour.
 *
 * Filled by phase 5: `useDemoAiReply` registers a handler that shows a typing
 * beat, asks `/public/demo-ai-reply` for a line, and writes it into the store
 * via `upsertDemoComment`. It stays a registered seam rather than a direct call
 * so `demoPostComment` has no opinion about — and no dependency on — what
 * replies; a test can register nothing, or a synchronous stub, and the mutation
 * path is unchanged.
 *
 * Whatever is registered must keep the invariant at the top of this file: the
 * reply may only be written into the demo store (or fetched from a dedicated
 * demo-only endpoint that writes nothing) — never posted through the real
 * collaboration comment API.
 */
export const setDemoAiReplyHandler = (
  handler: DemoAiReplyHandler | null
): void => {
  aiReplyHandler = handler
}

const notifyAiReplyHandler = (context: DemoAiReplyContext): void => {
  try {
    aiReplyHandler?.(context)
  } catch (error) {
    // A broken demo flourish must never break the approval flow.
    console.warn('[external-review] demo AI reply handler failed', error)
  }
}

/* -------------------------------------------------------------------------- */
/* Reset                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Clear the visitor's progress and drop the memoised snapshot, so the next read
 * rebuilds the pristine curated landing state from the demo link.
 */
export const resetDemo = (): void => {
  clearDemoStore()
  snapshotPromise = null
}
